/**
 * Body-profile sync for the health calculators. Signed-in users get their
 * height/weight/age/sex/etc. remembered once and auto-filled across all
 * health tools (stored locally in the browser vault, IndexedDB).
 *
 * Prefill runs post-mount only (never during SSR/hydration) and only before
 * the user edits anything, so the deterministic server-rendered defaults
 * and in-progress edits are never clobbered.
 */

import { Link, useLocation } from "@tanstack/react-router";
import { CheckCircle2, LogIn } from "lucide-react";
import { useEffect, useRef } from "react";
import { useVaultRecord } from "@/features/vault/use-vault-record";
import { m } from "@/paraglide/messages";
import { type BodyProfile, mergeProfile, profileMatches } from "./body-profile";

const NAMESPACE = "body-profile";
const KEY = "default";

interface BodyProfileSyncOptions {
  /** Push profile values into the calculator's input state (called once,
   * after the vault loads, before any user edit). */
  apply: (profile: BodyProfile) => void;
  /** Read the calculator's current inputs as profile fields (invalid
   * inputs should map to undefined so they never overwrite the profile). */
  collect: () => Partial<BodyProfile>;
  /** Input state the calculator syncs — save-back triggers on change. */
  deps: unknown[];
}

/**
 * Two-way sync between a health calculator's local input state and the
 * shared body profile. Returns flags for rendering BodyProfileHint.
 */
export function useBodyProfileSync({
  apply,
  collect,
  deps,
}: BodyProfileSyncOptions) {
  const record = useVaultRecord<BodyProfile>(NAMESPACE, KEY);
  const { signedIn, isLoaded, value, save } = record;

  const applyRef = useRef(apply);
  applyRef.current = apply;
  const collectRef = useRef(collect);
  collectRef.current = collect;
  const valueRef = useRef(value);
  valueRef.current = value;

  const prefillDoneRef = useRef(false);
  const editedRef = useRef(false);

  // Mark user edits that happen before the vault finished loading, so a
  // late prefill never overwrites what the user is typing.
  const initialDepsRef = useRef<unknown[] | null>(null);
  if (initialDepsRef.current === null) initialDepsRef.current = deps;
  if (
    !prefillDoneRef.current &&
    initialDepsRef.current.some((d, i) => d !== deps[i])
  ) {
    editedRef.current = true;
  }

  // One-time prefill once the profile is available.
  useEffect(() => {
    if (prefillDoneRef.current || !signedIn || !isLoaded) return;
    prefillDoneRef.current = true;
    if (value && !editedRef.current) applyRef.current(value);
  }, [signedIn, isLoaded, value]);

  // Debounced save-back of edited inputs (no-op when nothing changed).
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps is the caller's input state.
  useEffect(() => {
    if (!signedIn || !isLoaded || !prefillDoneRef.current) return;
    const timer = setTimeout(() => {
      const update = collectRef.current();
      if (profileMatches(valueRef.current ?? null, update)) return;
      save(mergeProfile(valueRef.current ?? null, update));
    }, 800);
    return () => clearTimeout(timer);
  }, [signedIn, isLoaded, save, ...deps]);

  return { signedIn, hasProfile: !!value };
}

/** One-line status under the inputs: sign-in nudge or "remembered" note. */
export function BodyProfileHint({
  signedIn,
  hasProfile,
}: {
  signedIn: boolean;
  hasProfile: boolean;
}) {
  const { pathname } = useLocation();
  if (!signedIn) {
    return (
      <Link
        to="/login"
        search={{ redirectUrl: pathname }}
        className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <LogIn className="h-3 w-3" aria-hidden="true" />
        {m.health_profile_signin_hint()}
      </Link>
    );
  }
  if (!hasProfile) return null;
  return (
    <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
      <CheckCircle2 className="h-3 w-3 text-success" aria-hidden="true" />
      {m.health_profile_saved()}
    </p>
  );
}
